/**
 * GeneratedSectionBands - a Tiptap extension that draws non-persistent background "bands"
 * behind whole sections in the editor, mirroring the read-only view's block-level
 * generated-content bands (conditional sections, AI-generated prose). It uses ProseMirror
 * Decorations (an overlay), so the saved document is never changed.
 *
 * It also renders the inline HEADING CONTROLS on section headings - a single widget after the
 * heading text holding (in order) the LOCK glyph, the override "Updated" annotation (a click-to-
 * reset badge + "edits are saved as an override" hint when the section's data table is overridden),
 * and the Delete / Restore control on CONDITIONAL headings. Rendering all of these in one widget
 * (and suppressing the heading's own `::after` lock via `rte-band--has-controls`) keeps the order
 * [heading text][lock][Updated][Delete]. A deleted conditional section is struck through.
 *
 * The host (the SOW editor) supplies, per render, the banded heading texts + the per-heading data
 * override state (read live via getter options the host keeps current), and dispatches a
 * transaction when any of it changes so the decorations recompute.
 */
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { slugifyHeading } from '@protoss/shared';
export const EMPTY_SECTION_BANDS = { conditional: [], ai: [], deleted: [] };
const bandsKey = new PluginKey('generatedSectionBands');
function headingTextOf(node) {
    if (node.type.name !== 'heading')
        return null;
    return node.textContent.trim() || null;
}
/**
 * Has the user hand-edited this AI section vs its generated original? `originalText` was stamped
 * over the PRE-tokenized markdown, so it carries literal `{{...}}` strings, but in the live editor
 * those are `protossToken` ATOM nodes that contribute NOTHING to `node.textContent`. So project the
 * live tokens back to `{{token}}` (via textBetween's leafText) before comparing - otherwise any AI
 * section containing a token always reads as "edited" (and never clears after a revert).
 */
function aiSectionEdited(node) {
    const original = String(node.attrs.originalText ?? '').trim();
    if (original === '')
        return false;
    const live = node
        .textBetween(0, node.content.size, '', (leaf) => leaf.type.name === 'protossToken' ? `{{${String(leaf.attrs.token ?? '')}}}` : '')
        .trim();
    return live !== original;
}
/** Run an async control action with a button spinner: clears the button label for a spinner +
 *  disables it; on rejection restores the label + a retry tooltip (success re-decorates the widget). */
function runWithSpinner(btn, action, retryTip) {
    if (btn.disabled)
        return;
    const label = btn.textContent;
    btn.disabled = true;
    btn.classList.add('rte-section-toggle--pending');
    const spinner = document.createElement('span');
    spinner.className = 'rte-token__spinner';
    spinner.setAttribute('aria-hidden', 'true');
    btn.textContent = '';
    btn.appendChild(spinner);
    // `.then(action)` (not `Promise.resolve(action())`) so a SYNCHRONOUS throw from action also
    // becomes a rejection the catch handles - else a sync failure escapes + leaves a stuck spinner.
    Promise.resolve()
        .then(() => action())
        .catch(() => {
        btn.disabled = false;
        btn.classList.remove('rte-section-toggle--pending');
        btn.textContent = label;
        btn.setAttribute('data-rte-tooltip', retryTip);
    });
}
/** Don't let a control's mousedown move the selection into the (locked) heading or blur the editor. */
function guardMouseDown(el) {
    el.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
    });
}
/**
 * The single inline controls widget at the end of a section heading: [lock][Updated override
 * annotation][Delete/Restore]. Only the parts that apply are rendered; the lock is always first
 * (these headings are read-only, and the heading's own `::after` lock is suppressed).
 */
/** An "Updated" reset/revert badge (accent, click runs the action with a spinner). */
function annoBadge(tooltip, action, retryTip) {
    const badge = document.createElement('button');
    badge.type = 'button';
    badge.className = 'rte-heading-anno__badge';
    badge.textContent = 'Updated';
    badge.setAttribute('contenteditable', 'false');
    badge.setAttribute('data-rte-tooltip', tooltip);
    guardMouseDown(badge);
    badge.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        runWithSpinner(badge, action, retryTip);
    });
    return badge;
}
function headingControls(opts) {
    const group = document.createElement('span');
    group.className = 'rte-heading-controls';
    group.setAttribute('contenteditable', 'false');
    const lock = document.createElement('span');
    lock.className = 'rte-section-toggle__lock';
    lock.setAttribute('aria-hidden', 'true');
    group.appendChild(lock);
    // Data-table status: a spinner while saving/resetting, a retry marker on failure, else the
    // "Updated" badge (click to reset) + the editable hint.
    if (opts.dataOverride) {
        const { dataKey, overridden, drift, saving, error, getOnReset } = opts.dataOverride;
        if (saving) {
            const spinner = document.createElement('span');
            spinner.className = 'rte-token__spinner';
            spinner.setAttribute('aria-hidden', 'true');
            group.appendChild(spinner);
        }
        else if (error) {
            const err = document.createElement('span');
            err.className = 'rte-heading-anno__error';
            err.textContent = '!';
            err.setAttribute('data-rte-tooltip', 'Save failed - edit again to retry');
            err.setAttribute('aria-label', 'Save failed');
            group.appendChild(err);
        }
        else if (overridden) {
            group.appendChild(annoBadge(drift
                ? 'Edited - may not match the deal data. Click to reset.'
                : 'Click to reset this table to the deal data', () => getOnReset()?.(dataKey), 'Reset failed - try again'));
            const hint = document.createElement('span');
            hint.className = 'rte-heading-anno__hint';
            hint.textContent = 'edits are saved as an override';
            group.appendChild(hint);
        }
    }
    // Edited AI section: an "Updated" badge that reverts to the original generated text.
    if (opts.aiEdit) {
        const { slug, getOnRevert } = opts.aiEdit;
        group.appendChild(annoBadge('Edited from the generated text. Click to revert.', () => getOnRevert()?.(slug), 'Revert failed - try again'));
    }
    // Delete / Restore on conditional headings.
    if (opts.toggle) {
        const { deleted, getOnToggle } = opts.toggle;
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `rte-section-toggle${deleted ? ' rte-section-toggle--restore' : ''}`;
        btn.textContent = deleted ? 'Restore' : 'Delete';
        btn.setAttribute('contenteditable', 'false');
        btn.setAttribute('data-rte-tooltip', deleted
            ? 'Restore this section to the SOW'
            : 'Delete this optional section from this SOW (you can restore it)');
        guardMouseDown(btn);
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            runWithSpinner(btn, () => getOnToggle()?.(slugifyHeading(opts.ht), !deleted), `${deleted ? 'Restore' : 'Delete'} failed - try again`);
        });
        group.appendChild(btn);
    }
    return group;
}
function buildDecorations(doc, bands, getOnToggle, getOverride, getOnReset, getOnRevertAi) {
    const conditional = new Set(bands.conditional);
    const ai = new Set(bands.ai);
    const deleted = new Set(bands.deleted ?? []);
    const overrideOf = getOverride();
    const onRevertAi = getOnRevertAi();
    // Pre-pass: which headings have an EDITED AI section beneath them (the AI node comes AFTER its
    // heading, so the main walk needs this up front to render the heading's "Updated" revert badge).
    // heading text -> the edited AI section's slug. We track a STACK of open ancestor headings and
    // attribute the edit to the heading whose slug matches the AI section's slug (its true anchor) -
    // so a DEEPER sub-heading between the anchor heading and the AI node doesn't steal the badge -
    // falling back to the nearest open heading when no slug matches.
    const aiEditedByHeading = new Map();
    if (onRevertAi) {
        const stack = [];
        doc.forEach((node) => {
            const ht = headingTextOf(node);
            if (ht !== null) {
                const level = typeof node.attrs.level === 'number' ? node.attrs.level : 1;
                while (stack.length > 0 && stack[stack.length - 1].level >= level)
                    stack.pop();
                stack.push({ level, text: ht });
                return;
            }
            if (node.type.name === 'sowAiSection' && aiSectionEdited(node)) {
                const aiSlug = String(node.attrs.slug ?? '');
                const anchor = stack.find((h) => slugifyHeading(h.text) === aiSlug) ?? stack[stack.length - 1];
                if (anchor)
                    aiEditedByHeading.set(anchor.text, aiSlug);
            }
        });
    }
    const decos = [];
    let active = null;
    doc.forEach((node, offset) => {
        const from = offset;
        const to = offset + node.nodeSize;
        const ht = headingTextOf(node);
        if (ht !== null) {
            const level = typeof node.attrs.level === 'number' ? node.attrs.level : 1;
            // A heading of equal/higher level ends the current section's band.
            if (active && level <= active.level)
                active = null;
            const isCond = conditional.has(ht);
            const isDeleted = isCond && deleted.has(ht);
            const bandCls = isDeleted
                ? 'rte-band rte-band--deleted'
                : isCond
                    ? 'rte-band rte-band--conditional'
                    : ai.has(ht)
                        ? 'rte-band rte-band--ai'
                        : null;
            if (bandCls)
                active = { level, cls: bandCls };
            // Inline controls for this heading: Delete/Restore (conditional) and/or the data-table
            // override "Updated" annotation. A deleted section drops its own controls (Restore aside).
            const toggle = isCond && getOnToggle() ? { deleted: isDeleted, getOnToggle } : null;
            const ov = !isDeleted && overrideOf ? overrideOf(ht) : null;
            const dataOverride = ov && getOnReset()
                ? {
                    dataKey: ov.dataKey,
                    overridden: !!ov.overridden,
                    drift: !!ov.drift,
                    saving: !!ov.saving,
                    error: !!ov.error,
                    getOnReset,
                }
                : null;
            const aiSlug = !isDeleted ? aiEditedByHeading.get(ht) : undefined;
            const aiEdit = aiSlug !== undefined && onRevertAi ? { slug: aiSlug, getOnRevert: getOnRevertAi } : null;
            const hasControls = !!toggle || !!dataOverride || !!aiEdit;
            // The heading node decoration: the band class (when banded) + the marker class that
            // suppresses the heading's own ::after lock (the lock is rendered inside the controls widget).
            const headClsParts = [];
            if (bandCls)
                headClsParts.push(bandCls, 'rte-band--head');
            if (hasControls)
                headClsParts.push('rte-band--has-controls');
            if (headClsParts.length > 0) {
                decos.push(Decoration.node(from, to, { class: headClsParts.join(' ') }));
            }
            if (hasControls) {
                const ovKey = dataOverride
                    ? `ov:${dataOverride.dataKey}:${dataOverride.overridden ? 'o' : ''}${dataOverride.saving ? 's' : ''}${dataOverride.error ? 'e' : ''}${dataOverride.drift ? 'd' : ''}`
                    : 'no';
                const aiKey = aiEdit ? `ai:${aiEdit.slug}` : 'no';
                decos.push(Decoration.widget(to - 1, () => headingControls({ ht, toggle, dataOverride, aiEdit }), {
                    side: 1,
                    key: `hc-${ht}-${isDeleted ? 'del' : 'on'}-${ovKey}-${aiKey}`,
                }));
            }
            return;
        }
        if (active)
            decos.push(Decoration.node(from, to, { class: active.cls }));
    });
    return DecorationSet.create(doc, decos);
}
export const GeneratedSectionBands = Extension.create({
    name: 'generatedSectionBands',
    addOptions() {
        return {
            getBands: () => EMPTY_SECTION_BANDS,
            getOnToggleDelete: () => null,
            getHeadingOverride: () => null,
            getOnResetData: () => null,
            getOnRevertAi: () => null,
        };
    },
    addProseMirrorPlugins() {
        const getBands = () => this.options.getBands();
        const getOnToggle = () => this.options.getOnToggleDelete();
        const getOverride = () => this.options.getHeadingOverride();
        const getOnReset = () => this.options.getOnResetData();
        const getOnRevertAi = () => this.options.getOnRevertAi();
        return [
            new Plugin({
                key: bandsKey,
                props: {
                    decorations: (state) => buildDecorations(state.doc, getBands(), getOnToggle, getOverride, getOnReset, getOnRevertAi),
                },
            }),
        ];
    },
});
//# sourceMappingURL=generatedSectionBands.js.map